import { isValid } from "date-fns";
import { Component, computed, effect, inject, input, signal, untracked } from "@angular/core";
import { takeUntilDestroyed, toObservable } from "@angular/core/rxjs-interop";
import { EMPTY, from } from "rxjs";
import { catchError, distinctUntilChanged, filter, switchMap } from "rxjs/operators";

import { ActionButton } from "@bitwarden/web-vault/app/components/buttons/gloss-button/actionButton";
import { actionButtonOptions } from "@bitwarden/web-vault/app/gloss/manage/manage-accounts/accounts-add-edit/component.options";
import { ConfirmationDialogService } from "@bitwarden/web-vault/app/services/confirmation/confirmation.service";
import { DataPluginStoreService } from "@bitwarden/web-vault/app/services/store/data-plugin/data-plugin.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";

import { InstitutionAccountView } from "@bitwarden/web-vault/app/models/view/institution/institution-account.view";
import { InstitutionView } from "@bitwarden/web-vault/app/models/view/institution/institution.view";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import {
  InstitutionAccountLink,
  InstitutionOriginEnum,
} from "@bitwarden/web-vault/app/models/types/institution.type";
import { COUNTRIES } from "@bitwarden/web-vault/app/models/constants/global.constants";
import {
  isSelectableCatalogInstitution,
  isSelectableInstitutionId,
} from "@bitwarden/web-vault/app/shared/utils/helper.institution/selectable-institution.helper";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";

@Component({
  selector: "app-institution-details",
  templateUrl: "./institution-details.component.html",
})
export class InstitutionDetailsComponent {
  // Services
  private readonly userStoreService = inject(UserStoreService);
  private readonly dataPluginStoreService = inject(DataPluginStoreService);
  private readonly i18nService = inject(I18nService);
  private readonly confirmationDialogService = inject(ConfirmationDialogService);

  // Inputs
  readonly institutionId = input<string>();

  // Writable UI state (signals)
  readonly countryCode = signal<string>("");
  readonly institutionCountry = signal<string>("");
  readonly institutionName = signal<string>("");
  readonly linkedInstitutionId = signal<string | null>(null);
  readonly selectedProductId = signal<string | null>(null);

  // Any field submit() saves: name, country, linked institution and product. The name counts when
  // the user edited it and the trimmed name submit() saves differs from the stored one: case-only
  // renames and removing whitespace saved before names were trimmed count, adding whitespace doesn't.
  readonly isDirty = computed<boolean>(() => {
    const inst = this.institutionView();
    if (!inst) {
      return false;
    }
    return (
      (this.institutionName() !== (inst.name ?? "") &&
        this.institutionName().trim() !== (inst.name ?? "")) ||
      this.countryCode() !== (inst.swift?.countryCode ?? "") ||
      this.isLinkChanged()
    );
  });

  private readonly isLinkChanged = computed<boolean>(() => {
    const linkTo = this.institutionView()?.linkTo;
    return (
      this.linkedInstitutionId() !== (linkTo?.id ?? null) ||
      this.selectedProductId() !== (linkTo?.productId ?? null)
    );
  });

  // The stored view, only read here (submit() edits a clone). Not a clone itself: clones drop the
  // owner (toStoreModel() has no oid), which submit() needs.
  readonly institutionView = computed<InstitutionView | null>(() => {
    const id = this.institutionId();
    return (
      this.userStoreService.institutions.institutionViews().find((inst) => inst.id === id) ?? null
    );
  });

  readonly isDuplicateName = computed<boolean>(() => {
    const name = this.institutionName().trim();
    const currentId = this.institutionId();

    // Empty name is handled by nameControl, return false to avoid unnecessary checks
    if (!name) {
      return false;
    }

    // In the same vault and country (Add Institution scopes by country too): a personal institution
    // and its team copy (forkAccountForOrg keeps the name), or one name in two countries, aren't
    // duplicates.
    const ownerId = this.institutionView()?.getOrganizationId() ?? null;
    const countryCode = this.countryCode();
    const existingInstitutions = this.userStoreService.institutions.institutionViews();
    return existingInstitutions.some((inst) => {
      return (
        (inst.getOrganizationId() ?? null) === ownerId &&
        inst.swift?.countryCode === countryCode &&
        // Only ones the user created ("you have created this institution before"). Catalog and
        // plugin institutions saved here are auto: linking a manual "ANZ" saves the catalog ANZ, and
        // connecting a plugin saves its institution (e.g. Opal), which must not block saving (#6314).
        inst.origin === InstitutionOriginEnum.manual &&
        inst.id !== currentId &&
        inst.name.trim().toLowerCase() === name.toLowerCase()
      );
    });
  });

  readonly nameControl = computed<boolean>(() => {
    const name = this.institutionName().trim();
    return !!name && name.length > 0;
  });

  // A link to a plugin or GLOSS Vault institution, saved before #6013, shows as no link: saving
  // then clears it and moves the accounts it moved back (isDirty sees the difference).
  private readonly savedLink = computed(() => {
    const linkTo = this.institutionView()?.linkTo;
    return linkTo && isSelectableInstitutionId(linkTo.id) ? linkTo : null;
  });

  // Sync UI signals when institutionView changes (deferred to avoid NG0600)
  // No allowSignalWrites needed, since writes happen in microtask.
  readonly initFormSync = effect(() => {
    const inst = this.institutionView();
    if (!inst) {
      return;
    }

    const linkTo = this.savedLink();
    queueMicrotask(() => {
      const current = untracked(() => ({
        cc: this.countryCode(),
        name: this.institutionName(),
        ctry: this.institutionCountry(),
        linked: this.linkedInstitutionId(),
        prod: this.selectedProductId(),
      }));

      const next = {
        cc: inst.swift?.countryCode ?? "",
        name: inst.name ?? "",
        ctry: this.getCountryNameOfInsto(inst),
        linked: linkTo?.id ?? null,
        prod: linkTo?.productId ?? null,
      };

      if (current.cc !== next.cc) {
        this.countryCode.set(next.cc);
      }
      if (current.name !== next.name) {
        this.institutionName.set(next.name);
      }
      if (current.ctry !== next.ctry) {
        this.institutionCountry.set(next.ctry);
      }
      if (current.linked !== next.linked) {
        this.linkedInstitutionId.set(next.linked);
      }
      if (current.prod !== next.prod) {
        this.selectedProductId.set(next.prod);
      }
    });
  });

  // Derived selections
  readonly selectedInstitution = computed<InstitutionView | null>(() => {
    const id = this.linkedInstitutionId();
    if (!id) {
      return null;
    }

    const nocodbViews =
      this.dataPluginStoreService.theInstitutionList?.theInstitutionViews?.() ?? [];
    const inst =
      this.userStoreService.institutions.institutionViews().find((i) => i.id === id) ??
      nocodbViews.find((i) => i.id === id) ??
      null;
    // Never a plugin or GLOSS Vault institution (#6013), even if linkedInstitutionId names one.
    return inst && isSelectableCatalogInstitution(inst) ? inst : null;
  });

  readonly selectedProduct = computed<InstitutionAccountView | null>(() => {
    const inst = this.selectedInstitution();
    const prodId = this.selectedProductId();
    if (!inst || !prodId) {
      return null;
    }
    return inst.availableAccounts.find((p) => p.id === prodId) ?? null;
  });

  readonly productOptions = computed<string[]>(() => {
    const inst = this.selectedInstitution();
    return inst?.availableAccounts?.map((a) => a.name) ?? [];
  });

  readonly countryOptions = computed<string[]>(() => {
    const inst = this.institutionView();
    if (!inst) {
      return [];
    }
    if (inst.origin === InstitutionOriginEnum.manual) {
      return COUNTRIES.map((c) => c.name);
    }
    return [this.getCountryNameOfInsto(inst)];
  });

  // Catalog institutions this one can be linked to. Shared by institutionOptions() and
  // linkedInstitutionChange() so a picked name resolves to the option it came from.
  private readonly linkableInstitutions = computed<InstitutionView[]>(() => {
    const code = this.countryCode();
    const institutions =
      this.dataPluginStoreService.theInstitutionList?.theInstitutionViews?.() ?? [];
    return institutions.filter(
      (i) => isSelectableCatalogInstitution(i) && i.swift?.countryCode === code,
    );
  });

  readonly institutionOptions = computed<string[]>(() =>
    this.linkableInstitutions().map((i) => i.name),
  );

  // Shared with a team (#6316): the accounts a link moves would point at a catalog institution the
  // org vault doesn't have (org copies get their own ids), so other members would see them without
  // an institution and sharing one on would fail. Linking is blocked; an old link can still be
  // cleared (changing the country), which moves its accounts back.
  readonly isOrgOwned = computed<boolean>(() => !!this.institutionView()?.isOrganizationOwned());

  // The user changed the link but its product isn't chosen (or can't be resolved yet, e.g. while
  // the catalog for that country is still loading). Saving it would hide the institution from Add
  // Account without moving its accounts. An unchanged saved link doesn't block other edits, even if
  // it can't be resolved right now. An org institution can't take a new link at all (isOrgOwned).
  readonly isLinkIncomplete = computed<boolean>(() => {
    if (!this.linkedInstitutionId()) {
      return false;
    }
    return this.isLinkChanged() && (this.isOrgOwned() || !this.selectedProduct());
  });

  // The saved link is cleared with Remove link: saving removes it and moves its accounts back. Not
  // after a country change, which links to the old country's catalog can't follow.
  readonly isLinkRemovalPending = computed<boolean>(
    () =>
      !!this.savedLink() &&
      !this.linkedInstitutionId() &&
      this.countryCode() === this.institutionView()?.swift?.countryCode,
  );

  readonly isSubmitting = signal(false);

  readonly saveButtonOptions = computed(() => {
    const isManual = this.institutionView()?.origin === InstitutionOriginEnum.manual;

    const disabled = !isManual || this.isSubmitting() || !this.isDirty() || this.isLinkIncomplete();

    return new ActionButton({
      ...actionButtonOptions,
      text: this.i18nService.t(this.isSubmitting() ? "saving" : "save"),
      isEnabled: !disabled,
      onClick: () => this.submit(),
    });
  });

  // Fetch institutions on country code changes (constructor = injection context; avoids NG0203/NG0600)
  constructor() {
    toObservable(this.countryCode)
      .pipe(
        filter((code): code is string => Boolean(code)),
        distinctUntilChanged(),
        filter(
          (code) => !this.dataPluginStoreService.theInstitutionList.countryCode().includes(code),
        ),
        switchMap((code) =>
          from(this.dataPluginStoreService.theInstitutionList.fetch({ countryCode: code })).pipe(
            catchError((e: unknown) => {
              const msg = e instanceof Error ? e.message : String(e);
              this.confirmationDialogService.notify("error", "failed", msg);
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(),
      )
      .subscribe();
  }

  // --- Handlers (only place we write to UI signals) ---

  institutionNameChange(name: string): void {
    this.institutionName.set(name);
  }

  institutionCountryChange(countryName: string): void {
    const code = COUNTRIES.find((c) => c.name === countryName)?.code ?? "";
    // The dropdown emits for the already-selected option too; that mustn't clear the link.
    if (code === this.countryCode()) {
      return;
    }
    this.countryCode.set(code);
    this.linkedInstitutionId.set(null);
    this.selectedProductId.set(null);
  }

  linkedInstitutionChange(linkedInstitutionName: string): void {
    const inst = this.linkableInstitutions().find((i) => i.name === linkedInstitutionName);
    // The dropdown emits for the already-selected option too; that mustn't clear the product.
    if ((inst?.id ?? null) === this.linkedInstitutionId()) {
      return;
    }
    this.linkedInstitutionId.set(inst?.id ?? null);
    this.selectedProductId.set(null);
  }

  /** Clears the link (the only link change an org institution takes, #6316). Saving it moves the
   * accounts it moved back. */
  linkCleared(): void {
    this.linkedInstitutionId.set(null);
    this.selectedProductId.set(null);
  }

  /** Undoes a Remove link before saving: back to the saved link. */
  linkRestored(): void {
    const linkTo = this.savedLink();
    this.linkedInstitutionId.set(linkTo?.id ?? null);
    this.selectedProductId.set(linkTo?.productId ?? null);
  }

  linkedProductChange(productName: string): void {
    const product = this.selectedInstitution()?.availableAccounts.find(
      (p) => p.name === productName,
    );
    this.selectedProductId.set(product?.id ?? null);
  }

  // --- Actions ---

  private async submit(): Promise<void> {
    try {
      this.isSubmitting.set(true);

      const currentInstitutionView = this.institutionView();
      // The Save button is disabled for an incomplete link; this only guards other callers.
      if (!currentInstitutionView || this.isLinkIncomplete()) {
        return;
      }

      const isNameValid = this.nameControl();
      const isDuplicate = this.isDuplicateName();

      if (isNameValid && isDuplicate) {
        this.confirmationDialogService.notify(
          "error",
          "error",
          "youHaveCreatedThisInstitutionBefore",
        );
        this.isSubmitting.set(false);
        return;
      }

      if (!isNameValid) {
        this.confirmationDialogService.notify("error", "error", "invalidInstitutionName");
        this.isSubmitting.set(false);
        return;
      }

      const view = currentInstitutionView;
      const ownerId = view.getOrganizationId() ?? null;
      // An org institution's accounts never move to a link: they'd point at an institution the org
      // vault doesn't have (#6316). It can only have an old link cleared, which moves them back.
      const canLink = !this.isOrgOwned();
      const institution = view.clone();
      const code = this.countryCode();

      institution.name = this.institutionName().trim();
      institution.bic.countryCode = code;
      institution.swift.countryCode = code;

      // No linked institution chosen → unlinked (null, not { id: "" }, see #6016).
      const linkedInstitutionId = this.linkedInstitutionId();
      institution.linkTo = linkedInstitutionId
        ? { id: linkedInstitutionId, productId: this.selectedProductId() ?? null }
        : null;

      // Read before the awaits, so the accounts are re-pointed to what is saved.
      const linkedInstitution = this.selectedInstitution();
      const linkedProduct = this.selectedProduct();
      // Accounts the link moved only move again when it changes: a rename mustn't reset the product
      // picked since for one of them (#6315).
      const linkChanged = this.isLinkChanged();

      // A catalog institution the user doesn't have yet is saved first, as Add Account does:
      // accounts only resolve institutions in the user's vault. On its own, so a failure here
      // changes nothing (a partial write of both could save the link without its target). On every
      // save, so one missing from the vault (e.g. removed since) is put back.
      if (linkedInstitution && linkedProduct && canLink && !linkedInstitution.isSyncInVault()) {
        await this.userStoreService.institutions.saveToVault([linkedInstitution.clone()], true);
      }
      await this.userStoreService.institutions.saveToVault([institution], true);
      try {
        if (linkedInstitution && linkedProduct && canLink) {
          await this.moveAccountsToLink(
            institution.id,
            ownerId,
            { institutionId: linkedInstitution.id, institutionAccountId: linkedProduct.id },
            linkChanged,
          );
        } else if (!linkedInstitutionId) {
          await this.restoreLinkedAccounts(institution.id, ownerId);
        }
        // Otherwise the saved link can't be resolved right now, or it's an org one: leave them.
      } catch (err) {
        // Put the institution back to match its accounts; the form follows it, so the user can redo
        // the change. Accounts a partial write already moved are still found by rootInstitutionLink.
        // A failing rollback mustn't hide the error that caused it.
        await this.userStoreService.institutions.saveToVault([view], true).catch(() => undefined);
        throw err;
      }

      this.confirmationDialogService.notify("success", "succeeded", "updatedSuccessfully");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      this.confirmationDialogService.notify("error", "failed", msg);
    } finally {
      this.isSubmitting.set(false);
    }
  }

  /** The institution's accounts, and those its link moved elsewhere. rootInstitutionLink records
   * where an account came from; it's cleared when the account is moved by hand or restored. Only
   * accounts with the institution's owner: an org copy forked before forks cleared it can still
   * carry a personal institution's root. */
  private linkedAccounts(
    institutionId: string,
    ownerId: string | null,
  ): { direct: AccountView[]; movedByLink: AccountView[] } {
    const accounts = this.userStoreService.accounts
      .accountViews()
      .filter((acc) => (acc.getOrganizationId() ?? null) === ownerId);
    return {
      direct: accounts.filter((acc) => acc.institutionLink?.institutionId === institutionId),
      movedByLink: accounts.filter(
        (acc) =>
          acc.rootInstitutionLink?.institutionId === institutionId &&
          acc.institutionLink?.institutionId !== institutionId,
      ),
    };
  }

  /** Points the institution's accounts at the linked product: always those still directly on it (a
   * linked institution keeps none, e.g. after a move that partly failed). Those an earlier link
   * moved: all of them when the link changed; otherwise only those left on another institution,
   * since one still on the linked institution may have had another product picked by hand (#6315). */
  private async moveAccountsToLink(
    institutionId: string,
    ownerId: string | null,
    link: InstitutionAccountLink,
    linkChanged: boolean,
  ): Promise<void> {
    const { direct, movedByLink } = this.linkedAccounts(institutionId, ownerId);
    // Unless the link changed, only those a relink that partly failed left on another institution:
    // moving one by hand clears its root (setAccountInstitutionLink).
    const stale = movedByLink.filter(
      (a) => linkChanged || a.institutionLink?.institutionId !== link.institutionId,
    );
    await this.saveAccounts(direct.concat(stale), (a) => {
      // Remember where it came from, so unlinking can put it back.
      if (a.institutionLink?.institutionId === institutionId) {
        a.rootInstitutionLink = a.institutionLink;
      }
      a.institutionLink = { ...link };
    });
  }

  /** Unlinked (e.g. a country change cleared the link): points the accounts the link moved back. */
  private async restoreLinkedAccounts(
    institutionId: string,
    ownerId: string | null,
  ): Promise<void> {
    await this.saveAccounts(this.linkedAccounts(institutionId, ownerId).movedByLink, (a) => {
      a.institutionLink = a.rootInstitutionLink;
      a.rootInstitutionLink = null;
    });
  }

  private async saveAccounts(
    accounts: AccountView[],
    update: (account: AccountView) => void,
  ): Promise<void> {
    if (accounts.length === 0) {
      return;
    }
    await this.userStoreService.accounts.saveToVault(
      accounts.map((acc) => {
        const a = acc.clone();
        update(a);
        return a;
      }),
      true,
    );
  }

  // --- Utils ---

  private getCountryNameOfInsto(institution: InstitutionView | null): string {
    if (!institution) {
      return "";
    }
    return COUNTRIES.find((c) => c.code === institution.swift?.countryCode)?.name ?? "";
  }
}
